import { Controller } from "stimulus";
import FroalaEditor from "froala-editor";
import "./froala-plugins";

export default class extends Controller {
  static targets = ["content", "editorWrapper"];

  connect() {
    this.isReadOnly = this.data.get("readOnly") === "true";
    this.isProcessing = false; // Add state management
    
    this.mergeTagValues = new Map();

    if (this.data.get("aiTextGenerator") && this.data.get("editAiContent")) {
      this.isReadOnly = false;
    }

    if (!this.isReadOnly) {
      this.richTextContentDivs = this.element.querySelectorAll(".rich-text-content");
      this.editorWrappers = this.element.querySelectorAll(".froala-editor-wrapper");
      this.addEventListeners();
    }
  }

  addEventListeners() {
    this.richTextContentDivs.forEach(div => {
      div.addEventListener("click", (event) => this.showEditor(event, div));
    });

    document.addEventListener("click", (event) => {
      // ✅ Prevent hiding if click is inside the file upload UI
      if (event.target.closest('.fr-popup') || event.target.closest('.fr-modal') || event.target.closest('.fr-find-replace-layer') || event.target.closest('.fr-toggle-advanced-options') || event.target.closest('.fr-button-group') ) {
        return;
      }

      // Check if click is on another rich text content div (switching editors)
      const clickedRichTextContent = event.target.closest('.rich-text-content');
      if (clickedRichTextContent && clickedRichTextContent !== event.target) {
        console.log('🔄 Click detected on another rich text content - switching editors');
        // Don't hide current editors, let the new editor show
        return;
      }

      this.editorWrappers.forEach(editorWrapper => {
        if (!editorWrapper.contains(event.target) && !editorWrapper.classList.contains("hidden")) {
          // Check if click is on another editor's content div
          if (this.isClickingOnAnotherEditor(event.target)) {
            console.log('🔄 Click detected on another editor - preventing hide');
            return;
          }
          
          this.hideEditor(editorWrapper);
        }
      });
    });

    // Add click handler for editor wrapper to ensure focus
    this.editorWrappers.forEach(editorWrapper => {
      editorWrapper.addEventListener("click", (event) => {
        if (!editorWrapper.classList.contains("hidden")) {
          const textArea = editorWrapper.querySelector(".froala");
          if (textArea && textArea.froalaEditor) {
            textArea.froalaEditor.events.focus();
          }
        }
      });
    });
  }

  showEditor(event, contentDiv) {
    if (this.isReadOnly || this.isProcessing) return;

    if (contentDiv) {
      // Hide all other editors first to prevent conflicts
      this.hideAllOtherEditors(contentDiv);
      
      const editorWrapper = contentDiv.nextElementSibling;

      if (editorWrapper && editorWrapper.classList.contains("froala-editor-wrapper")) {
        this.isProcessing = true;
        
        const textArea = editorWrapper.querySelector(".froala");
        
        // Hide content div immediately to prevent raw HTML
        contentDiv.style.display = "none";
        
        // Show editor wrapper - let CSS handle the styling
        editorWrapper.classList.remove("hidden");
        editorWrapper.style.display = "block";
        
        // If editor doesn't exist, initialize it
        if (!textArea.froalaEditor) {
          Promise.all([
            this.fetchMergeTags(editorWrapper),
          ])
          .then(([mergeTags, mergeTagValues]) => {
                    this.mergeTagValues = new Map(Object.entries(mergeTagValues));
            
            this.initializeFroala(editorWrapper, mergeTags);
            
            // After initialization, prepare content and focus
            setTimeout(() => {
              this.prepareEditorContent(contentDiv, textArea);
              if (textArea.froalaEditor) {
                this.positionCursorAtEnd(textArea);
              }
              this.isProcessing = false;
            }, 100);
          })
          .catch(error => {
            console.error("❌ ERROR: Failed to fetch merge tags or values (editor):", error);
            this.isProcessing = false;
          });
        } else {
          // Editor exists - preserve existing content and just focus
          // Don't call prepareEditorContent to avoid overwriting styled content
          
          // Ensure the editor is properly restored
          if (textArea.froalaEditor && textArea.froalaEditor.isDestroyed) {
            // If editor was destroyed, reinitialize
            Promise.all([
              this.fetchMergeTags(editorWrapper),
            ])
            .then(([mergeTags, mergeTagValues]) => {
                      this.mergeTagValues = new Map(Object.entries(mergeTagValues));
              
              this.initializeFroala(editorWrapper, mergeTags);
              setTimeout(() => {
                this.prepareEditorContent(contentDiv, textArea);
                if (textArea.froalaEditor) {
                  this.positionCursorAtEnd(textArea);
                }
                this.isProcessing = false;
              }, 100);
            });
          } else {
            // Editor is valid - focus immediately
            this.positionCursorAtEnd(textArea);
            this.isProcessing = false;
          }
        }
      }
      event.stopPropagation();
    }
  }

  // Strong method to ensure editor focus
  ensureEditorFocus(editorWrapper) {
    const textArea = editorWrapper.querySelector(".froala");
    if (!textArea || !textArea.froalaEditor) return;

    // Check if editor is ready
    const isReady = textArea.dataset.editorReady === "true";
    if (!isReady) {
      console.log("Editor not ready, waiting...");
      setTimeout(() => this.ensureEditorFocus(editorWrapper), 100);
      return;
    }

    // Method 1: Direct DOM manipulation and focus
    try {
      // Force the editor to be visible and focused
      const editorElement = textArea.froalaEditor.el;
      if (editorElement) {
        editorElement.style.caretColor = 'black';
        editorElement.style.caretShape = 'block';
        editorElement.focus();
      }
      
      // Use Froala's focus method
      textArea.froalaEditor.events.focus();
      textArea.froalaEditor.selection.setAtEnd();
      
      // Force cursor to end
      const range = document.createRange();
      const selection = window.getSelection();
      if (editorElement && editorElement.firstChild) {
        range.selectNodeContents(editorElement);
        range.collapse(false); // false = collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      console.log("Focus method 1 successful");
      return;
    } catch (error) {
      console.log("Method 1 failed:", error);
    }

    // Method 2: Click and focus after short delay
    setTimeout(() => {
      try {
        const editorElement = textArea.froalaEditor.el;
        if (editorElement) {
          editorElement.click();
          editorElement.focus();
          textArea.froalaEditor.events.focus();
          textArea.froalaEditor.selection.setAtEnd();
        }
        console.log("Focus method 2 successful");
        return;
      } catch (error) {
        console.log("Method 2 failed:", error);
      }
    }, 150);

    // Method 3: Programmatic click and focus
    setTimeout(() => {
      try {
        // Create and dispatch a click event
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        const editorElement = textArea.froalaEditor.el;
        if (editorElement) {
          editorElement.dispatchEvent(clickEvent);
          editorElement.focus();
          textArea.froalaEditor.events.focus();
          textArea.froalaEditor.selection.setAtEnd();
        }
        console.log("Focus method 3 successful");
        return;
      } catch (error) {
        console.log("Method 3 failed:", error);
      }
    }, 300);

    // Method 4: Force textarea focus
    setTimeout(() => {
      try {
        textArea.focus();
        textArea.click();
        textArea.froalaEditor.events.focus();
        textArea.froalaEditor.selection.setAtEnd();
        console.log("Focus method 4 successful");
        return;
      } catch (error) {
        console.log("Method 4 failed:", error);
      }
    }, 500);

    // Method 5: Last resort - simulate user interaction
    setTimeout(() => {
      try {
        // Simulate a complete user interaction
        const editorElement = textArea.froalaEditor.el;
        if (editorElement) {
          // Mouse down
          editorElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
          // Mouse up
          editorElement.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
          // Click
          editorElement.dispatchEvent(new MouseEvent('click', { bubbles: true }));
          // Focus
          editorElement.focus();
          textArea.froalaEditor.events.focus();
          textArea.froalaEditor.selection.setAtEnd();
        }
        console.log("Focus method 5 successful");
      } catch (error) {
        console.log("All focus methods failed:", error);
      }
    }, 700);
  }

  // Method to format content for editor (preserves styling and links)
  formatContentForEditor(htmlContent) {
    if (!htmlContent) return '';
    
    // Create a temporary div to safely process HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Remove any script tags for security
    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove any style tags
    const styles = tempDiv.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    // Clean up common issues while preserving styling
    let cleanHtml = tempDiv.innerHTML
      .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs
      .replace(/<p><\/p>/g, '') // Remove empty paragraphs
      .trim();
    
    // If content is empty after cleaning, return empty string
    if (!cleanHtml || cleanHtml === '') {
      return '';
    }
    
    return cleanHtml;
  }

  // Best practice method to extract clean text content
  extractCleanText(element) {
    if (!element) return '';
    
    // Create a temporary div to safely extract text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = element.innerHTML;
    
    // Remove any script tags for security
    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove any style tags
    const styles = tempDiv.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    // Get text content and clean it
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up common HTML entities and whitespace
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    
    return text;
  }

  // Method to prepare editor content before showing
  prepareEditorContent(contentDiv, textArea) {
    if (!textArea) return;
    
    // Extract content from the content div
    const contentDivHtml = contentDiv.innerHTML;
    
    // If content div has styled content (not just plain text), use it directly
    if (contentDivHtml && contentDivHtml.trim() !== '' && 
        (contentDivHtml.includes('<') || contentDivHtml.includes('&'))) {
      // Content has HTML/styling - preserve it
      const cleanHtml = this.formatContentForEditor(contentDivHtml);
      
      // Set the value directly to the textarea
      textArea.value = cleanHtml;
      
      // If Froala editor exists, also set its content
      if (textArea.froalaEditor) {
        textArea.froalaEditor.html.set(cleanHtml);
      }
    } else {
      // Plain text content - extract clean text
      const cleanText = this.extractCleanText(contentDiv);
      
      if (cleanText && cleanText.trim() !== '' && !cleanText.includes('Edit...')) {
        // Set the value directly to the textarea
        textArea.value = cleanText;
        
        // If Froala editor exists, also set its content
        if (textArea.froalaEditor) {
          textArea.froalaEditor.html.set(cleanText);
        }
      }
    }
    
    // Force a small delay to ensure the value is set
    setTimeout(() => {
      if (textArea.froalaEditor && textArea.froalaEditor.html.get() !== textArea.value) {
        textArea.froalaEditor.html.set(textArea.value);
      }
    }, 10);
  }

  // Method to ensure proper cleanup
  cleanupEditor(editorWrapper) {
    if (!editorWrapper) return;
    
    const textArea = editorWrapper.querySelector(".froala");
    if (textArea && textArea.froalaEditor) {
      try {
        textArea.froalaEditor.destroy();
      } catch (error) {
        console.log("Editor already destroyed or not initialized");
      }
    }
    
    // Reset all styles
    editorWrapper.style.height = "";
    editorWrapper.style.minHeight = "72px";
    editorWrapper.style.maxHeight = "";
    editorWrapper.style.overflow = "";
    editorWrapper.style.opacity = "0";
    editorWrapper.style.visibility = "hidden";
    editorWrapper.style.display = "none";
    editorWrapper.classList.add("hidden");
  }

  // Method to ensure content div is always properly restored
  restoreContentDiv(contentDiv) {
    if (!contentDiv) return;
    
    // Always restore to normal document flow
    contentDiv.style.position = "";
    contentDiv.style.left = "";
    contentDiv.style.top = "";
    contentDiv.style.opacity = "1";
    contentDiv.style.pointerEvents = "";
    contentDiv.style.display = "block";
    
    // Ensure content div has some content
    if (!contentDiv.innerHTML || contentDiv.innerHTML.trim() === '') {
      contentDiv.innerHTML = '<span class="no-content">Edit...</span>';
    }
  }

  hideEditor(editorWrapper) {
    if (this.isReadOnly || this.isProcessing) return; // Prevent multiple operations
    
    this.isProcessing = true; // Set processing state
    
    // Find the content div
    const contentDiv = editorWrapper.previousElementSibling;
    
    if (contentDiv) {
      const textArea = editorWrapper.querySelector(".froala");
      
      // Get the current content from the editor BEFORE saving to prevent reversion
      let currentContent = '';
      if (textArea && textArea.froalaEditor) {
        currentContent = textArea.froalaEditor.html.get();
      } else if (textArea) {
        currentContent = textArea.value || '';
      }
      
      // Clean up action-text-attachment tags
      currentContent = currentContent.replace(/<action-text-attachment/g, '<img')
                                    .replace(/<\/action-text-attachment>/g, '')
                                    .replace(/url=/g, 'src=');
      
      // Update content div with the current editor content IMMEDIATELY
      if (currentContent && currentContent.trim() !== '') {
        contentDiv.innerHTML = this.formatContentForDisplay(currentContent);
      } else {
        contentDiv.innerHTML = '<span class="no-content">Edit...</span>';
      }
      
      // Save content to backend (async, but content div is already updated)
      this.saveContent(textArea);
      
      // Don't destroy the editor, just hide it to preserve state
      editorWrapper.classList.add("hidden");
      editorWrapper.style.display = "none";
      contentDiv.style.display = "block";
      
      this.isProcessing = false; // Reset processing state
    }
  }

  // Best practice method to format content for display
  formatContentForDisplay(htmlContent) {
    if (!htmlContent) return '';
    
    // Create a temporary div to safely process HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Remove any script tags for security
    const scripts = tempDiv.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove any style tags
    const styles = tempDiv.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    // Clean up common issues
    let cleanHtml = tempDiv.innerHTML
      .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs
      .replace(/<p><\/p>/g, '') // Remove empty paragraphs
      .replace(/^\s*<p>(.*?)<\/p>\s*$/s, '$1') // Remove wrapping p tags if content is simple
      .trim();
    
    // If content is empty after cleaning, return placeholder
    if (!cleanHtml || cleanHtml === '') {
      return '<span class="no-content">Edit...</span>';
    }
    
    return cleanHtml;
  }



  initializeFroala(editorWrapper, liquidTags) {
    setTimeout(() => {
      const textArea = editorWrapper.querySelector(".froala");
      if (!textArea) {
        console.error("🚨 Froala editor target not found inside editorWrapper!", editorWrapper);
        return;
      }

      // Force destroy any existing editor
      if (textArea.froalaEditor) {
        try {
          textArea.froalaEditor.destroy();
        } catch (error) {
          console.log("Destroyed existing editor");
        }
      }

      const dropdownCommandName = `liquidTagDropdown_${Date.now()}`;
      const dropdownOptions = liquidTags.reduce((options, tag, index) => {
        options[index] = tag.title;
        return options;
      }, {});

      // Register fontSize and textColor for quick insert
      // Create custom dropdowns similar to liquid tag button
      FroalaEditor.RegisterQuickInsertButton('fontSize', {
        title: "Font Size",
        icon: 'fontSize',
        focus: true,
        callback: function() {
          const editor = this;
          console.log('🔧 Font size quick insert button clicked');
          
          // Save the current selection NOW (before dropdown is shown)
          editor.selection.save();
          
          // Prevent quick insert from hiding (same as liquid tag)
          if (editor.quickInsert) {
            editor.quickInsert._keepVisible = true;
            editor.quickInsert._allowHide = false;
          }
          
          try {
            editor.events.focus();
            editor.selection.restore();
          } catch (error) {
            console.log('Could not focus editor:', error);
          }
          
          const selection = editor.selection.get();
          let left = 0;
          let top = 0;
          
          if (selection && selection.range) {
            const range = selection.range;
            const rect = range.getBoundingClientRect();
            // Use viewport coordinates directly (position: fixed uses viewport coordinates)
            left = rect.left;
            top = rect.bottom;
          } else {
            const editorRect = editor.el.getBoundingClientRect();
            left = editorRect.left;
            top = editorRect.top;
          }
          
          // Ensure position is visible
          if (top === 0 || left === 0 || isNaN(top) || isNaN(left)) {
            const editorRect = editor.el.getBoundingClientRect();
            left = editorRect.left + (editorRect.width / 2);
            top = editorRect.top + 50;
          }
          
          const existingDropdown = document.querySelector('.font-size-quick-insert-dropdown');
          if (existingDropdown) {
            existingDropdown.remove();
          }
          
          const dropdown = document.createElement('div');
          dropdown.className = 'font-size-quick-insert-dropdown';
          dropdown.style.cssText = `
            position: fixed;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 99999;
            padding: 8px;
            min-width: 150px;
            max-height: 300px;
            overflow-y: auto;
          `;
          
          const fontSizes = ['8', '9', '10', '11', '12', '14', '16', '18', '20', '24', '28', '32', '36', '48', '72'];
          fontSizes.forEach(size => {
            const option = document.createElement('div');
            option.style.cssText = `
              padding: 6px 12px;
              cursor: pointer;
              font-size: ${size}px;
              transition: background-color 0.2s;
            `;
            option.textContent = size + 'px';
            option.addEventListener('mouseenter', () => {
              option.style.backgroundColor = '#e9ecef';
            });
            option.addEventListener('mouseleave', () => {
              option.style.backgroundColor = 'transparent';
            });
            option.addEventListener('click', (e) => {
              e.stopPropagation();
              e.preventDefault();
              console.log('🔧 Applying font size:', size);
              
              // Remove dropdown first
              dropdown.remove();
              
              // Apply command at current cursor position
              try {
                // Save selection right before executing command to preserve cursor position
                const savedSelection = editor.selection.save();
                
                // Apply the font size command
                editor.commands.exec('fontSize', size);
                
                // Restore selection immediately after command to keep cursor in place
                editor.selection.restore(savedSelection);
              } catch (error) {
                console.error('Error applying font size:', error);
              }
              
              // Hide quick insert toolbar after selection
              if (editor.quickInsert) {
                editor.quickInsert._keepVisible = false;
                editor.quickInsert._allowHide = true;
                if (editor.quickInsert._originalHide) {
                  editor.quickInsert._originalHide();
                }
              }
            });
            dropdown.appendChild(option);
          });
          
          dropdown.style.left = left + 'px';
          dropdown.style.top = top + 'px';
          document.body.appendChild(dropdown);
          console.log('🔧 Font size dropdown created at:', left, top);
          
          setTimeout(() => {
            const closeHandler = (e) => {
              if (!dropdown.contains(e.target)) {
                dropdown.remove();
                document.removeEventListener('click', closeHandler);
                // Allow quick insert to hide
                if (editor.quickInsert) {
                  editor.quickInsert._keepVisible = false;
                  editor.quickInsert._allowHide = true;
                }
              }
            };
            document.addEventListener('click', closeHandler);
          }, 10);
        }
      });

      FroalaEditor.RegisterQuickInsertButton('textColor', {
        title: "Text Color",
        icon: 'textColor',
        focus: true,
        callback: function() {
          const editor = this;
          console.log('🔧 Text color quick insert button clicked');
          
          // Save the current selection NOW (before dropdown is shown)
          editor.selection.save();
          
          // Prevent quick insert from hiding (same as liquid tag)
          if (editor.quickInsert) {
            editor.quickInsert._keepVisible = true;
            editor.quickInsert._allowHide = false;
          }
          
          try {
            editor.events.focus();
            editor.selection.restore();
          } catch (error) {
            console.log('Could not focus editor:', error);
          }
          
          const selection = editor.selection.get();
          let left = 0;
          let top = 0;
          
          if (selection && selection.range) {
            const range = selection.range;
            const rect = range.getBoundingClientRect();
            // Use viewport coordinates directly (position: fixed uses viewport coordinates)
            left = rect.left;
            top = rect.bottom;
          } else {
            const editorRect = editor.el.getBoundingClientRect();
            left = editorRect.left;
            top = editorRect.top;
          }
          
          // Ensure position is visible
          if (top === 0 || left === 0 || isNaN(top) || isNaN(left)) {
            const editorRect = editor.el.getBoundingClientRect();
            left = editorRect.left + (editorRect.width / 2);
            top = editorRect.top + 50;
          }
          
          const existingDropdown = document.querySelector('.text-color-quick-insert-dropdown');
          if (existingDropdown) {
            existingDropdown.remove();
          }
          
          const dropdown = document.createElement('div');
          dropdown.className = 'text-color-quick-insert-dropdown';
          dropdown.style.cssText = `
            position: fixed;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 99999;
            padding: 8px;
          `;
          
          const colors = [
            '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
            '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
            '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
            '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
            '#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0',
            '#a61c00', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3c78d8', '#3d85c6', '#674ea7', '#a64d79',
            '#85200c', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#1155cc', '#0b5394', '#351c75', '#741b47',
            '#5b0f00', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#1c4587', '#073763', '#20124d', '#4c1130'
          ];
          
          const grid = document.createElement('div');
          grid.style.cssText = 'display: grid; grid-template-columns: repeat(10, 1fr); gap: 2px;';
          
          colors.forEach(color => {
            const colorBox = document.createElement('div');
            colorBox.style.cssText = `
              width: 20px;
              height: 20px;
              background-color: ${color};
              border: 1px solid #ccc;
              cursor: pointer;
              transition: transform 0.1s;
            `;
            colorBox.addEventListener('mouseenter', () => {
              colorBox.style.transform = 'scale(1.2)';
            });
            colorBox.addEventListener('mouseleave', () => {
              colorBox.style.transform = 'scale(1)';
            });
              colorBox.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🔧 Applying text color:', color);
                
                // Remove dropdown first
                dropdown.remove();
                
                // Apply command at current cursor position
                try {
                  // Save selection right before executing command to preserve cursor position
                  const savedSelection = editor.selection.save();
                  
                  // Apply the color command
                  const cmd = editor.commands.get('textColor') ? 'textColor' : 'foreColor';
                  editor.commands.exec(cmd, color);
                  
                  // Restore selection immediately after command to keep cursor in place
                  editor.selection.restore(savedSelection);
                } catch (error) {
                  console.error('Error applying text color:', error);
                }
                
                // Hide quick insert toolbar after selection
                if (editor.quickInsert) {
                  editor.quickInsert._keepVisible = false;
                  editor.quickInsert._allowHide = true;
                  if (editor.quickInsert._originalHide) {
                    editor.quickInsert._originalHide();
                  }
                }
              });
            grid.appendChild(colorBox);
          });
          
          dropdown.appendChild(grid);
          dropdown.style.left = left + 'px';
          dropdown.style.top = top + 'px';
          document.body.appendChild(dropdown);
          
          dropdown.appendChild(grid);
          dropdown.style.left = left + 'px';
          dropdown.style.top = top + 'px';
          document.body.appendChild(dropdown);
          console.log('🔧 Text color dropdown created at:', left, top);
          
          setTimeout(() => {
            const closeHandler = (e) => {
              if (!dropdown.contains(e.target)) {
                dropdown.remove();
                document.removeEventListener('click', closeHandler);
                // Allow quick insert to hide
                if (editor.quickInsert) {
                  editor.quickInsert._keepVisible = false;
                  editor.quickInsert._allowHide = true;
                }
              }
            };
            document.addEventListener('click', closeHandler);
          }, 10);
        }
      });

      FroalaEditor.DefineIcon(dropdownCommandName, { NAME: "code", SVG_KEY: "tags" });
      FroalaEditor.RegisterCommand(dropdownCommandName, {
        title: "Insert Liquid Tag",
        type: "dropdown",
        focus: true,
        options: dropdownOptions,
        callback: function (cmd, value) {
          const index = parseInt(value, 10); 
          const selectedTag = liquidTags[index]?.value;
          
          // Insert tag at current cursor position instead of appending to end
          try {
            // Restore the current selection
            this.selection.restore();
            
            // Get the current selection range
            const selection = this.selection.get();
            if (selection && selection.range) {
              // Insert the tag at the current cursor position
              const range = selection.range;
              const textNode = document.createTextNode(selectedTag);
              range.insertNode(textNode);
              
              // Move cursor to the end of the inserted text
              range.setStartAfter(textNode);
              range.setEndAfter(textNode);
              this.selection.set(range);
            } else {
              // Fallback: append tag at the end
              const currentContent = this.html.get();
              this.html.set(currentContent + selectedTag);
              // Force cursor to the very end after a short delay
              setTimeout(() => {
                this.selection.setAtEnd(this.el);
                this.selection.restore();
                this.events.focus();
              }, 10);
            }
            
            // Focus the editor to ensure cursor is visible and maintain focus
            setTimeout(() => {
              this.events.focus();
              this.selection.restore();
            }, 10);
          } catch (error) {
            console.error('Error inserting liquid tag:', error);
            // Fallback: use the HTML insert method
            this.html.insert(selectedTag);
          }
        }
      });

      const tableBorderCommandName = 'tableBorderFormat';
      // Define icon using PATH with SVG path data for a hollow grid/table with borders
      // Using stroke-only paths (no fill) - each line is separate
      FroalaEditor.DefineIcon(tableBorderCommandName, {
        PATH: 'M3 3h18v18h-18z M3 9h18 M3 15h18 M9 3v18 M15 3v18',
        fill: 'none'
      });
      
      FroalaEditor.RegisterCommand(tableBorderCommandName, {
        title: "Table Border Width",
        type: "dropdown",
        focus: false,
        undo: true,
        options: {
          '0': 'No Border (0px)',
          '0.5': 'Thin (0.5px)',
          '1': '1px',
          '2': '2px',
          '3': '3px',
          '4': '4px',
          '5': 'Thick (5px)'
        },
        callback: function (cmd, value) {
          const borderWidth = value === '0' ? '0' : value + 'px';
          const borderColor = '#ddd'; // Default border color
          
          // Find the table in the current selection
          const selection = this.selection.get();
          if (!selection || !selection.range) {
            const selectedElement = this.selection.element();
            if (selectedElement && selectedElement.tagName === 'TABLE') {
              this.applyTableBorders(selectedElement, borderWidth, borderColor);
              return;
            }
            let node = selection.range ? selection.range.commonAncestorContainer : null;
            if (node) {
              if (node.nodeType !== 1) {
                node = node.parentElement;
              }
              const table = node ? node.closest('table') : null;
              if (table && table.tagName === 'TABLE') {
                this.applyTableBorders(table, borderWidth, borderColor);
              }
            }
            return;
          }
          
          // Get the selected element and find the closest table
          let node = selection.range.commonAncestorContainer;
          if (node.nodeType !== 1) {
            node = node.parentElement;
          }
          
          // Find the closest table element
          let table = node.closest('table');
          
          if (table && table.tagName === 'TABLE') {
            this.applyTableBorders(table, borderWidth, borderColor);
          } else {
            const selectedElement = this.selection.element();
            if (selectedElement) {
              const fallbackTable = selectedElement.tagName === 'TABLE' 
                ? selectedElement 
                : selectedElement.closest('table');
              if (fallbackTable && fallbackTable.tagName === 'TABLE') {
                this.applyTableBorders(fallbackTable, borderWidth, borderColor);
              }
            }
          }
        },
        applyTableBorders: function(table, borderWidth, borderColor) {
          // Apply border width to all cells
          const cells = table.querySelectorAll('td, th');
          cells.forEach(function(cell) {
            if (borderWidth === '0') {
              cell.style.border = 'none';
              cell.style.borderWidth = '0';
              cell.removeAttribute('border');
            } else {
              cell.style.border = borderWidth + ' solid ' + borderColor;
              cell.style.borderWidth = borderWidth;
            }
          });
          
          if (borderWidth === '0') {
            table.style.border = 'none';
            table.style.borderWidth = '0';
            table.removeAttribute('border');
          } else {
            table.style.border = borderWidth + ' solid ' + borderColor;
            table.style.borderWidth = borderWidth;
          }
          
          table.style.borderCollapse = 'collapse';
          
          this.html.set(this.html.get());
        }
      });

    // Store controller reference for use in events
    const controller = this;
    
    // Liquid tag insertion is handled by the dropdown command above
    // Removed duplicate liquidTag quick insert button to prevent conflicts
    
    // Email element functionality removed to simplify the interface
    
    const mobileToolbar = {
      moreMisc:{
        buttons: [
          dropdownCommandName, 'bold', 'italic', 'fontSize', 'textColor', 'underline', 'insertLink', 'undo', 'redo', 'clearFormatting', 'fullscreen', 'insertTable', tableBorderCommandName, 'fontFamily', 'backgroundColor', 'align', 'formatOL', 'formatUL', 'lineHeight', 'outdent', 'indent', 'quote', 'insertImage', 'insertVideo', 'embedly', 'insertHR', 'print', 'selectAll'
        ],
        buttonsVisible : 8
      }
    };

        const froalaApiKey = document.querySelector('meta[name="froala-api-key"]')?.content;

      console.log("🔧 Initializing Froala with toolbar configuration...");

      new FroalaEditor(textArea, {
        key: froalaApiKey,
        theme: 'dark',
        dragInline: false,
        attribution: false,
        toolbarSticky: false,
        toolbarInline: true, // Switch to false to ensure full Quick Insert behavior
        toolbarVisibleWithoutSelection: false,
        charCounterCount: false,
        wordCounterCount: false,
        placeholderText: 'Click to edit...',
        imageUpload: true,
        fileUpload: true,
        insertLink: true,
        imageUploadURL: '/froala_file_uploader',
        fileUploadURL: '/froala_file_uploader',
        imageResize: true,
        imageUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        fileUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        imageEditButtons: ['imageStyle', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageLink', 'imageRemove'],
        imageResizeWithPercent: true,
        
        // Table edit buttons - appears when table is selected
        tableEditButtons: ['tableHeader', 'tableRemove', 'tableRows', 'tableColumns', 'tableCells', 'tableCellBackground', 'tableCellVerticalAlign', 'tableCellHorizontalAlign', tableBorderCommandName],
        
        toolbarButtons: mobileToolbar,
        
        // Quick insert configuration
        quickInsertEnabled: true,
        quickInsertButtons: ['image', 'table', 'link', 'fontSize', 'textColor', 'ol', 'ul', 'hr', 'quote', 'video', 'code'],
        quickInsertTags: ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'li', 'td', 'th'], // Allow quick insert in all these tags even with content
        
        // Enable plugins including quick insert
        pluginsEnabled: ['mentions', 'link', 'quickInsert', 'image', 'table', 'lists', 'quote', 'code_view', 'video', 'hr', 'fontSize', 'colors'],
        
        // Configure enter behavior for quick insert
        enter: 1, // Use ENTER_DIV for block-level elements
        
        // Add link configuration
        linkInsertButtons: ['linkBack'],
        linkAlwaysBlank: false,
        linkAutoPrefix: 'https://',
        linkCheckBlank: true,
        linkCheckRelAttribute: false,
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
        linkText: true,
        linkInsertCallback: function (link) {
          console.log('Link inserted:', link);
        },
        // Prevent automatic unlinking
        linkClickCallback: function (link) {
          // Prevent default unlinking behavior
          return false;
        },
        // Additional link settings to prevent issues
        linkList: [],
        linkMultipleStyles: false,
        linkNoFollow: false,
        
        // Quick insert link configuration
        quickInsertLinkButtons: ['linkBack', '|', 'linkByURL'],
        quickInsertLinkAlwaysBlank: true,
        quickInsertLinkAutoPrefix: 'https://',
        
        // Video configuration
        videoInsertButtons: ['videoBack', '|', 'videoByURL', 'videoEmbed'],
        videoUploadURL: '/froala_file_uploader',
        videoUploadParams: {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        
        // Quote configuration
        quoteButtons: ['quoteBack', '|', 'quoteStyle'],
        
        fileUploadMethod: 'POST',
        imageUploadMethod: 'POST',
        enableMatchCase: true,

        mentions: {
          enabled: true,
          trigger: '@',
          minLength: 1,
          maxLength: 20,
          showOnMobile: true
        },
        
        events: {
          'initialized': function () {
            textArea.dataset.froalaInitialized = "true";
            console.log("✅ Froala initialized with plugins:", this.opts.pluginsEnabled);
            console.log("✅ Link plugin available:", typeof this.link !== 'undefined');
            console.log("✅ Toolbar buttons:", this.toolbar);

            if (this.mentions && typeof this.mentions._init === 'function') {
              this.mentions._init();
            } else {
              if (window.FroalaEditor && window.FroalaEditor.PLUGINS && window.FroalaEditor.PLUGINS.mentions) {
                var mentionsPlugin = window.FroalaEditor.PLUGINS.mentions(this);
                if (mentionsPlugin && typeof mentionsPlugin._init === 'function') {
                  mentionsPlugin._init();
                  console.log('Mentions plugin manually initialized');
                }
              }
            }
            
            // COMPREHENSIVE SOLUTION: Override quick insert plugin's internal logic
            const editor = this;
            
            // Override the quick insert plugin's visibility check
            if (this.quickInsert) {
              // Store original methods
              this.quickInsert._allowHide = false;
              this.quickInsert._keepVisible = true;
              
              if (typeof this.quickInsert.hide === 'function') {
                this.quickInsert._originalHide = this.quickInsert.hide.bind(this.quickInsert);
              }
              if (typeof this.quickInsert.show === 'function') {
                this.quickInsert._originalShow = this.quickInsert.show.bind(this.quickInsert);
              }
              
              // Override hide method completely
              this.quickInsert.hide = function() {
                if (!this._keepVisible || this._allowHide) {
                  if (this._originalHide) {
                    this._originalHide();
                  }
                }
              };
              
              this.quickInsert.allowHide = function() {
                this._allowHide = true;
                this._keepVisible = false;
              };
              
              this.quickInsert.preventHide = function() {
                this._allowHide = false;
                this._keepVisible = true;
              };
              
              // Override the plugin's internal _checkVisibility method if it exists
              if (this.quickInsert._checkVisibility) {
                const originalCheck = this.quickInsert._checkVisibility.bind(this.quickInsert);
                this.quickInsert._checkVisibility = function() {
                  if (this._keepVisible) {
                    if (this._originalShow) {
                      this._originalShow();
                    }
                    return true;
                  }
                  return originalCheck();
                };
              }
              
              // Override the plugin's _isLineEmpty method if it exists
              if (this.quickInsert._isLineEmpty) {
                const originalIsEmpty = this.quickInsert._isLineEmpty.bind(this.quickInsert);
                this.quickInsert._isLineEmpty = function() {
                  if (this._keepVisible) {
                    return true;
                  }
                  return originalIsEmpty();
                };
              }
            }
            
            // Hook into selection changes - this is when Froala checks visibility
            const originalSelectionRestore = this.selection.restore;
            this.selection.restore = function() {
              const result = originalSelectionRestore.apply(this, arguments);
              
              setTimeout(() => {
                if (editor.quickInsert && editor.quickInsert._keepVisible) {
                  if (editor.quickInsert._originalShow) {
                    editor.quickInsert._originalShow();
                  }
                  const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                  if (quickInsertElement) {
                    // CRITICAL: Ensure fr-visible class is always present
                    quickInsertElement.classList.add('fr-visible');
                    
                    // Fix positioning if off-screen
                    const style = quickInsertElement.getAttribute('style') || '';
                    if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                        style.includes('left: -9999px') || style.includes('left:-9999px')) {
                      quickInsertElement.style.top = '';
                      quickInsertElement.style.left = '';
                    }
                    
                    quickInsertElement.style.display = 'flex';
                    quickInsertElement.style.visibility = 'visible';
                    quickInsertElement.style.opacity = '1';
                  }
                }
              }, 10);
              
              return result;
            };
            
            // Use MutationObserver to watch for fr-visible class removal and prevent it
            setTimeout(() => {
              const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
              if (quickInsertElement) {
                // CRITICAL: Capture initial top value when quick insert first appears
                const captureInitialTop = () => {
                  const inlineTop = quickInsertElement.style.top || '';
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const computedTop = computedStyle.top;
                  
                  let currentTop = inlineTop;
                  if (!currentTop || currentTop === 'auto' || currentTop === '') {
                    if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                      currentTop = computedTop;
                    }
                  }
                  
                  // Store the top value on the element to persist it
                  if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                    quickInsertElement.setAttribute('data-preserved-top', currentTop);
                  }
                };
                
                // Capture initial top value immediately
                captureInitialTop();
                
                const observer = new MutationObserver((mutations) => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    mutations.forEach((mutation) => {
                      // Watch for class changes - specifically when fr-visible is removed
                      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const currentClasses = quickInsertElement.className || '';
                        if (!currentClasses.includes('fr-visible')) {
                          // fr-visible class was removed - add it back immediately
                          quickInsertElement.classList.add('fr-visible');
                        }
                        // Capture top value when fr-visible is added
                        if (currentClasses.includes('fr-visible')) {
                          captureInitialTop();
                        }
                      }
                      
                      // Watch for style changes - prevent positioning off-screen and preserve top
                      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                        const style = quickInsertElement.getAttribute('style') || '';
                        const computedStyle = window.getComputedStyle(quickInsertElement);
                        
                        // Capture top value if it's being set
                        captureInitialTop();
                        
                        // If positioned off-screen, fix it
                        if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                            style.includes('left: -9999px') || style.includes('left:-9999px') ||
                            computedStyle.top === '-9999px' || computedStyle.left === '-9999px') {
                          // Reposition based on cursor
                          try {
                            const selection = editor.selection.get();
                            if (selection && selection.startContainer) {
                              const range = document.createRange();
                              range.setStart(selection.startContainer, selection.startOffset);
                              range.setEnd(selection.startContainer, selection.startOffset);
                              const rect = range.getBoundingClientRect();
                              const editorBox = editor.$box.get(0);
                              const editorRect = editorBox.getBoundingClientRect();
                              
                              if (rect && editorRect) {
                                const newLeft = (rect.left - editorRect.left) + 'px';
                                quickInsertElement.style.left = newLeft;
                                
                                // Restore preserved top value
                                const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                                if (preservedTop) {
                                  quickInsertElement.style.top = preservedTop;
                                } else {
                                  const newTop = (rect.top - editorRect.top - 5) + 'px';
                                  quickInsertElement.style.top = newTop;
                                  quickInsertElement.setAttribute('data-preserved-top', newTop);
                                }
                              } else {
                                quickInsertElement.style.top = '';
                                quickInsertElement.style.left = '';
                              }
                            } else {
                              quickInsertElement.style.top = '';
                              quickInsertElement.style.left = '';
                            }
                          } catch (e) {
                            quickInsertElement.style.top = '';
                            quickInsertElement.style.left = '';
                          }
                          // Ensure fr-visible class is present
                          quickInsertElement.classList.add('fr-visible');
                        } else {
                          // Update position to follow cursor horizontally while preserving top property
                          try {
                            // Get preserved top value
                            const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                            
                            // If no preserved top, try to capture it now
                            if (!preservedTop) {
                              captureInitialTop();
                            }
                            
                            const selection = editor.selection.get();
                            if (selection && selection.startContainer) {
                              const range = document.createRange();
                              range.setStart(selection.startContainer, selection.startOffset);
                              range.setEnd(selection.startContainer, selection.startOffset);
                              const rect = range.getBoundingClientRect();
                              const editorBox = editor.$box.get(0);
                              const editorRect = editorBox.getBoundingClientRect();
                              
                              if (rect && editorRect) {
                                // Only update left position to slide right with cursor
                                const newLeft = (rect.left - editorRect.left) + 'px';
                                quickInsertElement.style.left = newLeft;
                                
                                // ALWAYS restore the preserved top property
                                const topToRestore = quickInsertElement.getAttribute('data-preserved-top');
                                if (topToRestore && topToRestore !== 'auto' && !topToRestore.includes('-9999')) {
                                  quickInsertElement.style.top = topToRestore;
                                }
                              }
                            }
                          } catch (e) {
                            // If positioning fails, continue
                          }
                        }
                        
                        // Also check for other hiding methods
                        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || 
                            computedStyle.opacity === '0' || style.includes('display: none')) {
                          if (editor.quickInsert._originalShow) {
                            editor.quickInsert._originalShow();
                          }
                          quickInsertElement.style.display = 'flex';
                          quickInsertElement.style.visibility = 'visible';
                          quickInsertElement.style.opacity = '1';
                          quickInsertElement.classList.add('fr-visible');
                        }
                      }
                    });
                  }
                });
                
                observer.observe(quickInsertElement, {
                  attributes: true,
                  attributeFilter: ['style', 'class'],
                  childList: true,
                  subtree: true
                });
                
                editor.quickInsert._observer = observer;
              }
            }, 300);
            
            // Aggressive polling - check every 50ms for fr-visible class and positioning
            const keepVisibleInterval = setInterval(() => {
              if (editor.quickInsert && editor.quickInsert._keepVisible) {
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  const computedStyle = window.getComputedStyle(quickInsertElement);
                  const style = quickInsertElement.getAttribute('style') || '';
                  const hasVisibleClass = quickInsertElement.classList.contains('fr-visible');
                  
                  // Check if fr-visible class is missing
                  if (!hasVisibleClass) {
                    quickInsertElement.classList.add('fr-visible');
                  }
                  
                  // Check if positioned off-screen
                  if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                      style.includes('left: -9999px') || style.includes('left:-9999px') ||
                      computedStyle.top === '-9999px' || computedStyle.left === '-9999px') {
                    // Reposition based on cursor
                    try {
                      const selection = editor.selection.get();
                      if (selection && selection.startContainer) {
                        const range = document.createRange();
                        range.setStart(selection.startContainer, selection.startOffset);
                        range.setEnd(selection.startContainer, selection.startOffset);
                        const rect = range.getBoundingClientRect();
                        const editorBox = editor.$box.get(0);
                        const editorRect = editorBox.getBoundingClientRect();
                        
                        if (rect && editorRect) {
                          const newLeft = (rect.left - editorRect.left) + 'px';
                          const newTop = (rect.top - editorRect.top - 5) + 'px';
                          quickInsertElement.style.left = newLeft;
                          quickInsertElement.style.top = newTop;
                        } else {
                          quickInsertElement.style.top = '';
                          quickInsertElement.style.left = '';
                        }
                      } else {
                        quickInsertElement.style.top = '';
                        quickInsertElement.style.left = '';
                      }
                    } catch (e) {
                      quickInsertElement.style.top = '';
                      quickInsertElement.style.left = '';
                    }
                    quickInsertElement.classList.add('fr-visible');
                  } else {
                    // Update position to follow cursor horizontally while preserving vertical position
                    try {
                      const selection = editor.selection.get();
                      if (selection && selection.startContainer) {
                        const range = document.createRange();
                        range.setStart(selection.startContainer, selection.startOffset);
                        range.setEnd(selection.startContainer, selection.startOffset);
                        const rect = range.getBoundingClientRect();
                        const editorBox = editor.$box.get(0);
                        const editorRect = editorBox.getBoundingClientRect();
                        
                        if (rect && editorRect) {
                          // Update left position to follow cursor horizontally (slide right)
                          const newLeft = (rect.left - editorRect.left) + 'px';
                          quickInsertElement.style.left = newLeft;
                          
                          // CRITICAL: Read top property FIRST before any operations
                          const inlineTop = quickInsertElement.style.top || '';
                          const computedStyle = window.getComputedStyle(quickInsertElement);
                          const computedTop = computedStyle.top;
                          
                          // Use inline style if available, otherwise use computed style
                          let currentTop = inlineTop;
                          if (!currentTop || currentTop === 'auto' || currentTop === '') {
                            if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                              currentTop = computedTop;
                            }
                          }
                          
                          // Store the top value on the element to persist it
                          if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                            quickInsertElement.setAttribute('data-preserved-top', currentTop);
                          }
                          
                          // ALWAYS restore the top property - use preserved value if available
                          const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                          const topToUse = preservedTop || currentTop;
                          
                          if (topToUse && topToUse !== 'auto' && !topToUse.includes('-9999')) {
                            quickInsertElement.style.top = topToUse;
                          }
                        }
                      }
                    } catch (e) {
                      // If positioning fails, continue
                    }
                  }
                  
                  // Check for other hiding methods
                  if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || 
                      computedStyle.opacity === '0') {
                    if (editor.quickInsert._originalShow) {
                      editor.quickInsert._originalShow();
                    }
                    quickInsertElement.style.display = 'flex';
                    quickInsertElement.style.visibility = 'visible';
                    quickInsertElement.style.opacity = '1';
                    quickInsertElement.classList.add('fr-visible');
                  }
                }
              }
            }, 50);
            
            if (editor.quickInsert) {
              editor.quickInsert._keepVisibleInterval = keepVisibleInterval;
            }
            
            // Set content immediately to prevent raw HTML flickering
            if (textArea.value && textArea.value.trim() !== '') {
              this.html.set(textArea.value);
            }
            
            // Show the textarea with smooth transition
            textArea.style.visibility = 'visible';
            textArea.style.opacity = '1';
            
            // Focus and position cursor using the new method
            this.events.focus();
            this.selection.setAtEnd(this.el);
            this.selection.restore();
            
            // Initialize merge tag functionality
            controller.initializeMergeTagFunctionality(this);
          },
          'focus': function () {
            // Don't force cursor to end - preserve user's cursor position
            // Show merge tags as actual tags when focusing
            controller.showMergeTagsAsTags(this);
          },
          'keydown': function (key, event) {
            const editor = this;
            
            // Prevent hiding when typing
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
            }
            
            // Force show immediately on any key press - ensure fr-visible class stays and position updates
            const forceShowQuickInsert = () => {
              if (editor.quickInsert && editor.quickInsert._keepVisible) {
                if (editor.quickInsert._originalShow) {
                  editor.quickInsert._originalShow();
                }
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  // CRITICAL: Ensure fr-visible class is always present
                  quickInsertElement.classList.add('fr-visible');
                  quickInsertElement.classList.remove('fr-hidden');
                  
                  // Fix positioning if off-screen
                  const style = quickInsertElement.getAttribute('style') || '';
                  if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                      style.includes('left: -9999px') || style.includes('left:-9999px')) {
                    quickInsertElement.style.top = '';
                    quickInsertElement.style.left = '';
                  }
                  
                  // Ensure visibility
                  quickInsertElement.style.display = 'flex';
                  quickInsertElement.style.visibility = 'visible';
                  quickInsertElement.style.opacity = '1';
                  
                  // Update position to follow cursor horizontally
                  try {
                    const selection = editor.selection.get();
                    if (selection && selection.startContainer) {
                      const range = document.createRange();
                      range.setStart(selection.startContainer, selection.startOffset);
                      range.setEnd(selection.startContainer, selection.startOffset);
                      const rect = range.getBoundingClientRect();
                      const editorBox = editor.$box.get(0);
                      const editorRect = editorBox.getBoundingClientRect();
                      
                      if (rect && editorRect) {
                        // CRITICAL: Read top property FIRST before any operations
                        const inlineTop = quickInsertElement.style.top || '';
                        const computedStyle = window.getComputedStyle(quickInsertElement);
                        const computedTop = computedStyle.top;
                        
                        // Use inline style if available, otherwise use computed style
                        let currentTop = inlineTop;
                        if (!currentTop || currentTop === 'auto' || currentTop === '') {
                          if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                            currentTop = computedTop;
                          }
                        }
                        
                        // Store the top value on the element to persist it
                        if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                          quickInsertElement.setAttribute('data-preserved-top', currentTop);
                        }
                        
                        // Only update left position to slide right with cursor
                        const newLeft = (rect.left - editorRect.left) + 'px';
                        quickInsertElement.style.left = newLeft;
                        
                        // ALWAYS restore the top property - use preserved value if available
                        const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                        const topToUse = preservedTop || currentTop;
                        
                        if (topToUse && topToUse !== 'auto' && !topToUse.includes('-9999')) {
                          quickInsertElement.style.top = topToUse;
                        }
                      }
                    }
                  } catch (e) {
                    // If positioning fails, just ensure it's visible
                    console.log('Position update failed:', e);
                  }
                }
              }
            };
            
            if (key === 13) { // Enter key
              console.log('✅ Enter key pressed - keeping quick insert visible');
              setTimeout(forceShowQuickInsert, 5);
              setTimeout(forceShowQuickInsert, 50);
              setTimeout(forceShowQuickInsert, 100);
            } else {
              // Keep quick insert visible when typing any character
              setTimeout(forceShowQuickInsert, 5);
              setTimeout(forceShowQuickInsert, 50);
            }
          },
          
          'keyup': function() {
            const editor = this;
            if (editor.quickInsert && editor.quickInsert._keepVisible) {
              setTimeout(() => {
                if (editor.quickInsert._originalShow) {
                  editor.quickInsert._originalShow();
                }
                const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                if (quickInsertElement) {
                  // CRITICAL: Ensure fr-visible class is always present
                  quickInsertElement.classList.add('fr-visible');
                  
                  // Fix positioning if off-screen
                  const style = quickInsertElement.getAttribute('style') || '';
                  if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                      style.includes('left: -9999px') || style.includes('left:-9999px')) {
                    quickInsertElement.style.top = '';
                    quickInsertElement.style.left = '';
                  }
                  
                  quickInsertElement.style.display = 'flex';
                  quickInsertElement.style.visibility = 'visible';
                  quickInsertElement.style.opacity = '1';
                  
                  // Update position to follow cursor horizontally
                  try {
                    const selection = editor.selection.get();
                    if (selection && selection.startContainer) {
                      const range = document.createRange();
                      range.setStart(selection.startContainer, selection.startOffset);
                      range.setEnd(selection.startContainer, selection.startOffset);
                      const rect = range.getBoundingClientRect();
                      const editorBox = editor.$box.get(0);
                      const editorRect = editorBox.getBoundingClientRect();
                      
                      if (rect && editorRect) {
                        // CRITICAL: Read top property FIRST before any operations
                        const inlineTop = quickInsertElement.style.top || '';
                        const computedStyle = window.getComputedStyle(quickInsertElement);
                        const computedTop = computedStyle.top;
                        
                        // Use inline style if available, otherwise use computed style
                        let currentTop = inlineTop;
                        if (!currentTop || currentTop === 'auto' || currentTop === '') {
                          if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                            currentTop = computedTop;
                          }
                        }
                        
                        // Store the top value on the element to persist it
                        if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                          quickInsertElement.setAttribute('data-preserved-top', currentTop);
                        }
                        
                        // Only update left position to slide right with cursor
                        const newLeft = (rect.left - editorRect.left) + 'px';
                        quickInsertElement.style.left = newLeft;
                        
                        // ALWAYS restore the top property - use preserved value if available
                        const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                        const topToUse = preservedTop || currentTop;
                        
                        if (topToUse && topToUse !== 'auto' && !topToUse.includes('-9999')) {
                          quickInsertElement.style.top = topToUse;
                        }
                      }
                    }
                  } catch (e) {
                    // If positioning fails, just ensure it's visible
                    console.log('Position update failed:', e);
                  }
                }
              }, 10);
            }
          },

          'input': function () {
            // Keep quick insert visible when text is entered
            const editor = this;
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              // Force show multiple times to catch all cases - ensure fr-visible class and update position
              [5, 20, 50, 100].forEach(delay => {
                setTimeout(() => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    if (editor.quickInsert._originalShow) {
                      editor.quickInsert._originalShow();
                    }
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement) {
                      // CRITICAL: Ensure fr-visible class is always present
                      quickInsertElement.classList.add('fr-visible');
                      
                      // Fix positioning if off-screen
                      const style = quickInsertElement.getAttribute('style') || '';
                      if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                          style.includes('left: -9999px') || style.includes('left:-9999px')) {
                        quickInsertElement.style.top = '';
                        quickInsertElement.style.left = '';
                      }
                      
                      quickInsertElement.style.display = 'flex';
                      quickInsertElement.style.visibility = 'visible';
                      quickInsertElement.style.opacity = '1';
                      
                      // Update position to follow cursor horizontally
                      try {
                        const selection = editor.selection.get();
                        if (selection && selection.startContainer) {
                          const range = document.createRange();
                          range.setStart(selection.startContainer, selection.startOffset);
                          range.setEnd(selection.startContainer, selection.startOffset);
                          const rect = range.getBoundingClientRect();
                          const editorBox = editor.$box.get(0);
                          const editorRect = editorBox.getBoundingClientRect();
                          
                          if (rect && editorRect) {
                            // Get current top position to preserve it
                            const computedStyle = window.getComputedStyle(quickInsertElement);
                            const currentTop = computedStyle.top;
                            
                            // Update left position to follow cursor horizontally (slide right)
                            const newLeft = (rect.left - editorRect.left) + 'px';
                            quickInsertElement.style.left = newLeft;
                            
                            // Preserve vertical position (don't let it move down)
                            if (currentTop && currentTop !== 'auto' && currentTop !== '-9999px') {
                              quickInsertElement.style.top = currentTop;
                            } else if (!currentTop || currentTop === 'auto') {
                              // If no top position set, position it above cursor
                              const newTop = (rect.top - editorRect.top - 5) + 'px';
                              quickInsertElement.style.top = newTop;
                            }
                          }
                        }
                      } catch (e) {
                        // If positioning fails, just ensure it's visible
                        console.log('Position update failed:', e);
                      }
                    }
                  }
                }, delay);
              });
            }
          },

          'contentChanged': function () {
            // Keep quick insert visible after content changes
            const editor = this;
            if (editor.quickInsert) {
              editor.quickInsert._keepVisible = true;
              editor.quickInsert._allowHide = false;
              
              // Force show multiple times - ensure fr-visible class and update position
              [5, 20, 50, 100].forEach(delay => {
                setTimeout(() => {
                  if (editor.quickInsert && editor.quickInsert._keepVisible) {
                    if (editor.quickInsert._originalShow) {
                      editor.quickInsert._originalShow();
                    }
                    const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                    if (quickInsertElement) {
                      // CRITICAL: Ensure fr-visible class is always present
                      quickInsertElement.classList.add('fr-visible');
                      
                      // Fix positioning if off-screen
                      const style = quickInsertElement.getAttribute('style') || '';
                      if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                          style.includes('left: -9999px') || style.includes('left:-9999px')) {
                        quickInsertElement.style.top = '';
                        quickInsertElement.style.left = '';
                      }
                      
                      quickInsertElement.style.display = 'flex';
                      quickInsertElement.style.visibility = 'visible';
                      quickInsertElement.style.opacity = '1';
                      
                      // Update position to follow cursor horizontally
                      try {
                        const selection = editor.selection.get();
                        if (selection && selection.startContainer) {
                          const range = document.createRange();
                          range.setStart(selection.startContainer, selection.startOffset);
                          range.setEnd(selection.startContainer, selection.startOffset);
                          const rect = range.getBoundingClientRect();
                          const editorBox = editor.$box.get(0);
                          const editorRect = editorBox.getBoundingClientRect();
                          
                          if (rect && editorRect) {
                            // Get current top position to preserve it
                            const computedStyle = window.getComputedStyle(quickInsertElement);
                            const currentTop = computedStyle.top;
                            
                            // Update left position to follow cursor horizontally (slide right)
                            const newLeft = (rect.left - editorRect.left) + 'px';
                            quickInsertElement.style.left = newLeft;
                            
                            // Preserve vertical position (don't let it move down)
                            if (currentTop && currentTop !== 'auto' && currentTop !== '-9999px') {
                              quickInsertElement.style.top = currentTop;
                            } else if (!currentTop || currentTop === 'auto') {
                              // If no top position set, position it above cursor
                              const newTop = (rect.top - editorRect.top - 5) + 'px';
                              quickInsertElement.style.top = newTop;
                            }
                          }
                        }
                      } catch (e) {
                        // If positioning fails, just ensure it's visible
                        console.log('Position update failed:', e);
                      }
                    }
                  }
                }, delay);
              });
            }
          },
          
          'selection.changed': function() {
            // Hook into selection changes - this is when Froala checks quick insert visibility
            const editor = this;
            if (editor.quickInsert) {
              // Check if there's text at the cursor position (clicking on existing text)
              try {
                const selection = editor.selection.get();
                if (selection && selection.startContainer) {
                  // Check the entire line, not just the text node
                  let lineElement = selection.startContainer;
                  if (lineElement.nodeType === Node.TEXT_NODE) {
                    lineElement = lineElement.parentElement;
                  }
                  
                  // Walk up to find the paragraph or div containing the line
                  while (lineElement && lineElement !== editor.$box.get(0)) {
                    if (lineElement.tagName === 'P' || lineElement.tagName === 'DIV' || 
                        lineElement.classList.contains('fr-element')) {
                      break;
                    }
                    lineElement = lineElement.parentElement;
                  }
                  
                  const textContent = lineElement ? (lineElement.textContent || '') : '';
                  const isOnExistingText = textContent.trim().length > 0;
                  
                  // If clicking on existing text, set _keepVisible to true
                  if (isOnExistingText) {
                    editor.quickInsert._keepVisible = true;
                    editor.quickInsert._allowHide = false;
                  }
                }
              } catch (e) {
                // If we can't determine, assume we should show it
                editor.quickInsert._keepVisible = true;
                editor.quickInsert._allowHide = false;
              }
              
              // Show quick insert if _keepVisible is true
              if (editor.quickInsert._keepVisible) {
                setTimeout(() => {
                  if (editor.quickInsert._originalShow) {
                    editor.quickInsert._originalShow();
                  }
                  const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                  if (quickInsertElement) {
                    // CRITICAL: Ensure fr-visible class is always present
                    quickInsertElement.classList.add('fr-visible');
                    
                    // Fix positioning if off-screen
                    const style = quickInsertElement.getAttribute('style') || '';
                    if (style.includes('top: -9999px') || style.includes('top:-9999px') ||
                        style.includes('left: -9999px') || style.includes('left:-9999px')) {
                      quickInsertElement.style.top = '';
                      quickInsertElement.style.left = '';
                    }
                    
                    quickInsertElement.style.display = 'flex';
                    quickInsertElement.style.visibility = 'visible';
                    quickInsertElement.style.opacity = '1';
                    
                    // Update position to follow cursor horizontally
                    try {
                      const selection = editor.selection.get();
                      if (selection && selection.startContainer) {
                        const range = document.createRange();
                        range.setStart(selection.startContainer, selection.startOffset);
                        range.setEnd(selection.startContainer, selection.startOffset);
                        const rect = range.getBoundingClientRect();
                        const editorBox = editor.$box.get(0);
                        const editorRect = editorBox.getBoundingClientRect();
                        
                        if (rect && editorRect) {
                          // CRITICAL: Read top property FIRST before any operations
                          const inlineTop = quickInsertElement.style.top || '';
                          const computedStyle = window.getComputedStyle(quickInsertElement);
                          const computedTop = computedStyle.top;
                          
                          // Use inline style if available, otherwise use computed style
                          let currentTop = inlineTop;
                          if (!currentTop || currentTop === 'auto' || currentTop === '') {
                            if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                              currentTop = computedTop;
                            }
                          }
                          
                          // Store the top value on the element to persist it
                          if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                            quickInsertElement.setAttribute('data-preserved-top', currentTop);
                          }
                          
                          // Only update left position to slide right with cursor
                          const newLeft = (rect.left - editorRect.left) + 'px';
                          quickInsertElement.style.left = newLeft;
                          
                          // ALWAYS restore the top property - use preserved value if available
                          const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                          const topToUse = preservedTop || currentTop;
                          
                          if (topToUse && topToUse !== 'auto' && !topToUse.includes('-9999')) {
                            quickInsertElement.style.top = topToUse;
                          }
                        }
                      }
                    } catch (e) {
                      // If positioning fails, just ensure it's visible
                      console.log('Position update failed:', e);
                    }
                  }
                }, 10);
              }
            }
          },
          
          'mouseup': function() {
            // Show quick insert when clicking on existing text
            const editor = this;
            if (editor.quickInsert) {
              // Check if there's text at the cursor position
              setTimeout(() => {
                try {
                  const selection = editor.selection.get();
                  if (selection && selection.startContainer) {
                    // Check the entire line, not just the text node
                    let lineElement = selection.startContainer;
                    if (lineElement.nodeType === Node.TEXT_NODE) {
                      lineElement = lineElement.parentElement;
                    }
                    
                    // Walk up to find the paragraph or div containing the line
                    while (lineElement && lineElement !== editor.$box.get(0)) {
                      if (lineElement.tagName === 'P' || lineElement.tagName === 'DIV' || 
                          lineElement.classList.contains('fr-element')) {
                        break;
                      }
                      lineElement = lineElement.parentElement;
                    }
                    
                    const textContent = lineElement ? (lineElement.textContent || '') : '';
                    const isOnExistingText = textContent.trim().length > 0;
                    
                    // If clicking on existing text, show quick insert
                    if (isOnExistingText) {
                      editor.quickInsert._keepVisible = true;
                      editor.quickInsert._allowHide = false;
                      
                      if (editor.quickInsert._originalShow) {
                        editor.quickInsert._originalShow();
                      }
                      
                      const quickInsertElement = editor.$box ? editor.$box.find('.fr-quick-insert').get(0) : null;
                      if (quickInsertElement) {
                        quickInsertElement.classList.add('fr-visible');
                        quickInsertElement.style.display = 'flex';
                        quickInsertElement.style.visibility = 'visible';
                        quickInsertElement.style.opacity = '1';
                        
                        // Update position to follow cursor
                        try {
                          const range = document.createRange();
                          range.setStart(selection.startContainer, selection.startOffset);
                          range.setEnd(selection.startContainer, selection.startOffset);
                          const rect = range.getBoundingClientRect();
                          const editorBox = editor.$box.get(0);
                          const editorRect = editorBox.getBoundingClientRect();
                          
                          if (rect && editorRect) {
                            // CRITICAL: Read top property FIRST before any operations
                            const inlineTop = quickInsertElement.style.top || '';
                            const computedStyle = window.getComputedStyle(quickInsertElement);
                            const computedTop = computedStyle.top;
                            
                            // Use inline style if available, otherwise use computed style
                            let currentTop = inlineTop;
                            if (!currentTop || currentTop === 'auto' || currentTop === '') {
                              if (computedTop && computedTop !== 'auto' && !computedTop.includes('-9999')) {
                                currentTop = computedTop;
                              }
                            }
                            
                            // Store the top value on the element to persist it
                            if (currentTop && currentTop !== 'auto' && !currentTop.includes('-9999')) {
                              quickInsertElement.setAttribute('data-preserved-top', currentTop);
                            }
                            
                            // Only update left position to slide right with cursor
                            const newLeft = (rect.left - editorRect.left) + 'px';
                            quickInsertElement.style.left = newLeft;
                            
                            // ALWAYS restore the top property - use preserved value if available
                            const preservedTop = quickInsertElement.getAttribute('data-preserved-top');
                            const topToUse = preservedTop || currentTop;
                            
                            if (topToUse && topToUse !== 'auto' && !topToUse.includes('-9999')) {
                              quickInsertElement.style.top = topToUse;
                            }
                          }
                        } catch (e) {
                          console.log('Position update failed on mouseup:', e);
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.log('Error checking text on mouseup:', e);
                }
              }, 50);
            }
          },
          'blur': function () {
            // Replace tags with values when losing focus
            controller.replaceTagsWithValues(this);
            // Save content
            controller.saveContent(textArea);
          },
          'table.inserted': function (table) {
            // Set default 0.5px borders for newly inserted tables
            const cells = table.querySelectorAll('td, th');
            cells.forEach(function(cell) {
              cell.style.border = '0.5px solid #ddd';
            });
            table.style.border = '0.5px solid #ddd';
          },
          'image.beforeUpload': function (files) {
            console.log("🖼️ Image selected for upload:", files);
            customFileUpload(files[0], this, "image");
            return false; // Prevent Froala's default upload
          },
          'image.beforePasteUpload': function (img) {
            console.log("🛑 Preventing default pasted image insertion");
            detectAndUploadBase64Images(this);
            return false; // Prevent automatic base64 image insertion
          },
          'file.beforeUpload': function (files) {
            console.log("📄 File selected for upload:", files);
            customFileUpload(files[0], this, "file");
            return false; // Prevent Froala's default upload
          },
          'image.inserted': function ($img, response) {
            console.log("✔️ Image inserted into editor", $img, response);
            // Resize inserted images to max 500px width
            resizeImageToMaxWidth($img, 500);
          },
          'paste.after': function () {
            // Handle URL to link conversion and image resize after paste
            const editor = this;
            setTimeout(() => {
              // Convert URLs to links
              convertUrlsToLinks(editor);
              // Resize any pasted images
              resizePastedImages(editor, 500);
            }, 100);
          },
          'destroy': function () {
            delete textArea.dataset.froalaInitialized;
          }
        }
      });

      this.setupQuickInsertButtonSpacing();
    }, 0);
  }

  setupQuickInsertButtonSpacing() {
    const fixQuickInsertButtons = () => {
      const quickInsertContainer = document.querySelector('.fr-quick-insert');
      if (quickInsertContainer) {
        const buttons = quickInsertContainer.querySelectorAll('.fr-btn');
        
        buttons.forEach((button, index) => {
          button.style.margin = '0 8px 0 0';
          button.style.display = 'inline-block';
          button.style.verticalAlign = 'middle';
          
          button.style.transition = 'all 0.2s ease';
          
          button.style.position = 'relative';
          button.style.zIndex = '1';
        });
        
        quickInsertContainer.style.display = 'flex';
        quickInsertContainer.style.flexDirection = 'row';
        quickInsertContainer.style.alignItems = 'center';
        quickInsertContainer.style.gap = '8px';
      }
    };

    fixQuickInsertButtons();
    document.addEventListener('click', (event) => {
      if (event.target.closest('.fr-quick-insert') || event.target.closest('.fr-floating-btn')) {
        setTimeout(fixQuickInsertButtons, 100);
      }
    });
    
    setInterval(fixQuickInsertButtons, 2000);
  }

  saveContent(textArea) {
    console.log(textArea.value);
    const container = textArea.closest('.rich-text-container');
    if (!container) return;
    
    const { taskId, accountId, projectId, taskListId, blockId, contentType } = container.dataset;
    const content = textArea.value;

    fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        task_block: {
          [contentType]: content
        },
        skip_broadcast: true
      })
    })
    .then(response => response.json())
    .then(data => console.log("Content saved:", data))
    .catch(error => console.error("Error saving content:", error));
  }

  fetchMergeTags(editorWrapper) {
    return new Promise((resolve, reject) => {
      const textArea = editorWrapper.querySelector(".froala");
      const container = textArea.closest('.rich-text-container');
      if (!container) return reject(new Error("Rich text container not found."));

      const { accountId, projectId, taskListId, taskId, blockId, processTemplateId, contentType } = container.dataset;
      let url;

      if (taskId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/get_liquid_tags?block_id=${blockId}&content_type=${contentType}`;
      } else if (processTemplateId) {
        url = `/${accountId}/process_templates/${processTemplateId}/get_liquid_tags?block_id=${blockId}&content_type=${contentType}`;
      } else if (taskListId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/get_liquid_tags`;
      } else if (projectId) {
        url = `/${accountId}/projects/${projectId}/get_liquid_tags`;
      }
      else {
        url = `/${accountId}/client_assignments/get_liquid_tags`;
      }

      fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        }
      })
        .then(response => response.ok ? response.json() : Promise.reject(response.status))
        .then(data => resolve(data.map(tag => ({ value: tag[1], title: tag[0] }))))
        .catch(reject);
    });
  }

  fetchMergeTagValues(editorWrapper) {
    return new Promise((resolve, reject) => {
      const textArea = editorWrapper.querySelector(".froala");
      const container = textArea.closest('.rich-text-container');
      if (!container) {
        console.error("Rich text container not found");
        return reject(new Error("Rich text container not found."));
      }

      const { accountId, projectId, taskListId, taskId, blockId, processTemplateId, contentType } = container.dataset;
      
      let url;

      // For comment forms, we don't need liquid tags, just return empty object
      if (!blockId && !processTemplateId) {
        resolve({});
        return;
      }

      if (taskId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/get_merge_tag_values?block_id=${blockId}&content_type=${contentType}`;
      } else if (processTemplateId) {
        url = `/${accountId}/process_templates/${processTemplateId}/get_merge_tag_values?block_id=${blockId}&content_type=${contentType}`;
      } else if (taskListId) {
        url = `/${accountId}/projects/${projectId}/task_lists/${taskListId}/get_merge_tag_values`;
      } else if (projectId) {
        url = `/${accountId}/projects/${projectId}/get_merge_tag_values`;
      } else {
        url = `/${accountId}/client_assignments/get_merge_tag_values`;
      }

      fetch(url, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        }
      })
        .then(response => {
          if (!response.ok) {
            console.error("Response not ok:", response.status, response.statusText);
            return Promise.reject(response.status);
          }
          
          return response.json();
        })
        .then(data => {
          resolve(data);
        })
        .catch(error => {
          console.error("Error fetching merge tag values:", error);
          reject(error);
        });
    });
  }

  // Method to position cursor at the end of content
  positionCursorAtEnd(textArea) {
    if (!textArea || !textArea.froalaEditor) return;
    
    try {
      const editor = textArea.froalaEditor;
      
      // Method 1: Use Froala's built-in method with element reference
      editor.selection.setAtEnd(editor.el);
      editor.selection.restore();
      
      // Method 2: Force focus and position
      editor.events.focus();
      editor.selection.setAtEnd();
      
      // Method 3: Direct DOM manipulation as fallback
      setTimeout(() => {
        const editorElement = editor.el;
        if (editorElement) {
          const range = document.createRange();
          const selection = window.getSelection();
          
          // Find the last text node
          const walker = document.createTreeWalker(
            editorElement,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );
          
          let lastTextNode = null;
          let node;
          while (node = walker.nextNode()) {
            lastTextNode = node;
          }
          
          if (lastTextNode) {
            range.setStartAfter(lastTextNode);
            range.setEndAfter(lastTextNode);
          } else {
            // If no text nodes, position at end of editor
            range.selectNodeContents(editorElement);
            range.collapse(false);
          }
          
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
      
    } catch (error) {
      console.log("Cursor positioning failed:", error);
    }
  }

  initializeMergeTagFunctionality(editor) {
    // Store the original content to restore tags when focusing
    editor.originalContent = editor.html.get();
    
    // Replace tags with values initially
    this.replaceTagsWithValues(editor);
  }

  showMergeTagsAsTags(editor) {
    if (!this.mergeTagValues || this.mergeTagValues.size === 0) {
      return;
    }
    
    let content = editor.html.get();
    
    // Replace values back to tags
    this.mergeTagValues.forEach((value, tag) => {
      // Escape special regex characters in the value
      const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedValue, 'g');
      const newContent = content.replace(regex, tag);
      
      if (newContent !== content) {
        content = newContent;
      }
    });
    
    // Only update if content actually changed
    if (content !== editor.html.get()) {
      editor.html.set(content);
    }
  }

  replaceTagsWithValues(editor) {
    if (!this.mergeTagValues || this.mergeTagValues.size === 0) {
      return;
    }
    
    let content = editor.html.get();
    let hasChanges = false;
    
    // Replace tags with their values
    this.mergeTagValues.forEach((value, tag) => {
      if (content.includes(tag)) {
        const escapedTag = this.escapeRegExp(tag);
        content = content.replace(new RegExp(escapedTag, 'g'), value);
        hasChanges = true;
      }
    });
    
    // Only update if content actually changed
    if (hasChanges) {
      editor.html.set(content);
    }
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  isClickingOnAnotherEditor(target) {
    // Check if the click is on another rich text content div
    const clickedRichTextContent = target.closest('.rich-text-content');
    if (clickedRichTextContent) {
      // Check if this rich text content belongs to a different editor
      const clickedEditorWrapper = clickedRichTextContent.nextElementSibling;
      if (clickedEditorWrapper && clickedEditorWrapper.classList.contains('froala-editor-wrapper')) {
        // This is a different editor, don't hide current ones
        return true;
      }
    }
    
    // Check if click is on any other editor wrapper
    const clickedEditorWrapper = target.closest('.froala-editor-wrapper');
    if (clickedEditorWrapper) {
      // This is a different editor wrapper, don't hide current ones
      return true;
    }
    
    return false;
  }

  hideAllOtherEditors(currentContentDiv) {
    // Hide all other editors except the one being activated
    this.editorWrappers.forEach(editorWrapper => {
      const contentDiv = editorWrapper.previousElementSibling;
      if (contentDiv && contentDiv !== currentContentDiv && !editorWrapper.classList.contains("hidden")) {
        console.log('🔄 Hiding other editor to prevent conflicts');
        // Save content before hiding
        const textArea = editorWrapper.querySelector(".froala");
        if (textArea && textArea.froalaEditor) {
          this.saveContent(textArea);
        }
        this.hideEditor(editorWrapper);
      }
    });
  }
}

function customFileUpload(file, editor, type) {
  const reader = new FileReader();
  reader.onload = function (event) {
    const formData = new FormData();
    formData.append("authenticity_token", document.querySelector('meta[name="csrf-token"]').getAttribute('content'));
    formData.append("file", file);

    fetch(editor.opts.imageUploadURL, {
      method: "POST",
      body: formData
    })
      .then(response => response.json())
      .then(data => {
        console.log("Upload response:", data);
        if (data.success) {
          editor.selection.restore();

          if (type === "image") {
            // Insert a placeholder image with the SGID stored in a data attribute
            const imageTag = `<img src="${data.link}" data-attachment-sgid="${data.attachment_sgid}" />`;
            editor.html.insert(imageTag);
            // Resize inserted image to max 500px width
            setTimeout(() => {
              const insertedImg = editor.el.querySelector(`img[data-attachment-sgid="${data.attachment_sgid}"]`);
              if (insertedImg) {
                resizeImageToMaxWidth(insertedImg, 500);
              }
            }, 100);
          }

          console.log("✅ File uploaded successfully:", data);
        } else {
          console.error("❌ Upload failed:", data.error);
        }
      })
      .catch(error => {
        console.error("❌ Error uploading file:", error);
      });
  };
  reader.readAsDataURL(file);
}

function detectAndUploadBase64Images(editor) {
  const images = editor.el.querySelectorAll("img");
  images.forEach((img) => {
    if (img.src.startsWith("data:image")) {
      uploadBase64Image(img.src)
        .then((uploadedImageUrl) => {
          img.src = uploadedImageUrl;
          // Resize uploaded image to max 500px width
          resizeImageToMaxWidth(img, 500);
        })
        .catch((error) => console.error("Image upload failed", error));
    } else {
      // Resize existing images to max 500px width
      resizeImageToMaxWidth(img, 500);
    }
  });
}

async function uploadBase64Image(base64Data) {
  const response = await fetch("/upload_binary_image", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
    },
    body: JSON.stringify({ image: base64Data }),
  });

  if (!response.ok) throw new Error("Failed to upload image");
  const data = await response.json();
  return data.url;
}

// Function to resize image to maximum width while maintaining aspect ratio
function resizeImageToMaxWidth(img, maxWidth) {
  if (!img || !img.complete) {
    // Wait for image to load
    img.onload = function() {
      resizeImageToMaxWidth(img, maxWidth);
    };
    return;
  }
  
  if (img.naturalWidth > maxWidth) {
    const aspectRatio = img.naturalHeight / img.naturalWidth;
    img.style.maxWidth = maxWidth + 'px';
    img.style.width = maxWidth + 'px';
    img.style.height = 'auto';
    // Remove width/height attributes to use CSS instead
    img.removeAttribute('width');
    img.removeAttribute('height');
  }
}

// Function to resize all pasted images in the editor
function resizePastedImages(editor, maxWidth) {
  const images = editor.el.querySelectorAll("img");
  images.forEach((img) => {
    resizeImageToMaxWidth(img, maxWidth);
  });
}


function isPartOfEmail(text, matchIndex, url) {
  if (matchIndex < 0 || matchIndex >= text.length) return false;
  
  
  const before = matchIndex > 0 ? text[matchIndex - 1] : '';
  

  const lookbackStart = Math.max(0, matchIndex - 100);
  const textBefore = text.substring(lookbackStart, matchIndex);
  
  
  if (textBefore.includes('@')) {
    
    const emailPart = textBefore.substring(textBefore.lastIndexOf('@') + 1);
    
    if (emailPart.trim().length === 0 || emailPart.trim() === url.substring(0, emailPart.trim().length)) {
      return true;
    }
  }
  
  
  return before === '@';
}


function isValidDomainUrl(url) {
  
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return true;
  }
  
  
  let domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
  
  
  domain = domain.split('/')[0];
  
  
  if (!domain.includes('.')) {
    return false;
  }
  
  
  const parts = domain.split('.');
  
 
  if (parts.length < 2) {
    return false;
  }
  
  
  const compoundTlds = new Set([
    'co.uk', 'com.au', 'co.nz', 'co.in', 'com.pk', 'co.za', 'com.br', 'com.mx',
    'co.jp', 'com.cn', 'co.kr', 'com.sg', 'com.hk', 'com.tw', 'com.my', 'com.ph',
    'co.th', 'com.vn', 'com.id', 'co.id', 'net.au', 'org.uk', 'gov.uk', 'ac.uk',
    'ltd.uk', 'plc.uk', 'net.nz', 'org.nz', 'gov.au', 'edu.au', 'asn.au', 'id.au'
  ]);
  
  
  if (parts.length >= 2) {
    const lastTwo = parts.slice(-2).join('.').toLowerCase();
    if (compoundTlds.has(lastTwo)) {
      return true;
    }
  }
  
 
  const tld = parts[parts.length - 1].toLowerCase();
  
  
  const validCcTlds = new Set([
    'us', 'uk', 'pk', 'de', 'fr', 'in', 'au', 'ca', 'cn', 'jp', 'sa', 'ae', 'eu', 'it', 'es', 'nl',
    'se', 'no', 'fi', 'dk', 'ch', 'at', 'be', 'pl', 'cz', 'ie', 'pt', 'gr', 'ro', 'hu', 'bg', 'hr',
    'sk', 'si', 'ee', 'lv', 'lt', 'lu', 'mt', 'cy', 'co', 'mx', 'br', 'ar', 'cl', 'pe', 'za',
    'ng', 'eg', 'ke', 'ma', 'tn', 'dz', 'ly', 'sd', 'et', 'ug', 'tz', 'gh', 'ci', 'sn', 'cm', 'ao',
    'mz', 'zw', 'bw', 'na', 'zm', 'mg', 'mu', 'sc', 'km', 'dj', 'er', 'so', 'rw', 'bi', 'td', 'ne',
    'ml', 'bf', 'sl', 'lr', 'tg', 'bj', 'mr', 'gm', 'gw', 'gn', 'cv', 'st', 'gq', 'ga', 'cg', 'cd',
    'cf', 'td', 'kh', 'la', 'mm', 'bn', 'sg', 'my', 'id', 'ph', 'vn', 'th', 'bd', 'lk', 'mv', 'np',
    'bt', 'mn', 'kz', 'uz', 'tj', 'tm', 'kg', 'af', 'ir', 'iq', 'sy', 'jo', 'lb', 'il', 'ps', 'ye',
    'om', 'qa', 'bh', 'kw', 'ae', 'tr', 'az', 'ge', 'am', 'by', 'ua', 'md', 'ru', 'is', 'fo', 'gl',
    'gi', 'ad', 'mc', 'sm', 'va', 'li', 'je', 'gg', 'im', 'ax', 'io', 'tv', 'cc', 'ws', 'as', 'gu',
    'mp', 'pr', 'vi', 'vg', 'ky', 'bm', 'fk', 'gs', 'sh', 'tc', 'pf', 'nc', 'pn', 'nf', 'cx', 'hm',
    'aq', 'tf', 'gf', 'gp', 'mq', 're', 'yt', 'pm', 'bl', 'mf', 'sx', 'cw', 'bq', 'aw', 'sj', 'eh'
  ]);
  
  
  if (tld.length === 2) {
    return validCcTlds.has(tld);
  }
  
  
  const validGenericTlds = new Set([
    'com', 'org', 'net', 'edu', 'gov', 'mil', 'int', 'info', 'biz', 'name', 'pro',
    'aero', 'coop', 'museum', 'jobs', 'mobi', 'travel', 'tel', 'asia', 'cat',
    'xxx', 'post', 'geo', 'app', 'dev', 'io', 'tech', 'online', 'site', 'website',
    'store', 'shop', 'blog', 'news', 'media', 'tv', 'me', 'cc', 'ws', 'ly', 'ai'
  ]);
  
  
  return validGenericTlds.has(tld);
}

// Function to convert URLs in text to links
function convertUrlsToLinks(editor) {
  const editorElement = editor.el;
  const textNodes = getTextNodes(editorElement);
  
  // Unicode-safe alphanumeric check (letters and numbers from any language)
  const isAlphaNumericUnicode = (char) => /[\p{L}\p{N}]/u.test(char);
  
  textNodes.forEach((textNode) => {
    const text = textNode.textContent;
    
    // URL regex pattern - matches potential URLs (validation happens later):
 
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s<>"']*)?|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s<>"']*)?)/gi;
    
    let match;
    const matches = [];
    const matchIndices = [];
    
    
    while ((match = urlRegex.exec(text)) !== null) {
      let url = match[0];
      const matchIndex = match.index;
      
      
      url = url.replace(/[.,;:!?]+$/, '');
      
      
      const charBefore = matchIndex > 0 ? text[matchIndex - 1] : '';
      const charAfter = matchIndex + url.length < text.length ? text[matchIndex + url.length] : '';
      
      
      if (charBefore && isAlphaNumericUnicode(charBefore)) {
        continue; 
      }
      if (charAfter && isAlphaNumericUnicode(charAfter)) {
        continue; 
      }
      
      matches.push(url);
      matchIndices.push(matchIndex);
    }
    
    if (matches.length > 0) {
      let newHTML = text;
      
      
      for (let i = matches.length - 1; i >= 0; i--) {
        const url = matches[i];
        const matchIndex = matchIndices[i];
        
       
        // 1. Reject if already inside a link
        let parent = textNode.parentNode;
        let isInsideLink = false;
        while (parent && parent !== editorElement) {
          if (parent.tagName === 'A') {
            isInsideLink = true;
            break;
          }
          parent = parent.parentNode;
        }
        
        if (isInsideLink) continue;
        
        
        if (isPartOfEmail(text, matchIndex, url)) continue;
        
        
        const charBefore = matchIndex > 0 ? text[matchIndex - 1] : '';
        const charAfter = matchIndex + url.length < text.length ? text[matchIndex + url.length] : '';
        if (charBefore && isAlphaNumericUnicode(charBefore)) continue;
        if (charAfter && isAlphaNumericUnicode(charAfter)) continue;
        
       
        const isValidUrl = isValidDomainUrl(url);
        
        if (isValidUrl) {
          // Ensure URL has protocol
          let fullUrl = url;
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            fullUrl = 'https://' + url;
          }
          
          // Replace URL with link (open in new window by default)
          const linkHTML = `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${url}</a>`;
          
          // Replace from the specific index to preserve other matches
          const beforeMatch = newHTML.substring(0, matchIndex);
          const afterMatch = newHTML.substring(matchIndex + url.length);
          newHTML = beforeMatch + linkHTML + afterMatch;
        }
      }
      
      if (newHTML !== text) {
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = newHTML;
        
        // Replace the text node with the new content
        const parent = textNode.parentNode;
        while (tempDiv.firstChild) {
          parent.insertBefore(tempDiv.firstChild, textNode);
        }
        parent.removeChild(textNode);
      }
    }
  });
  
  // Update Froala's internal HTML
  editor.html.set(editor.html.get());
}

// Helper function to get all text nodes in an element
function getTextNodes(element) {
  const textNodes = [];
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: function(node) {
        // Skip text nodes that are inside links
        let parent = node.parentNode;
        while (parent && parent !== element) {
          if (parent.tagName === 'A') {
            return NodeFilter.FILTER_REJECT;
          }
          parent = parent.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  return textNodes;
}